'use client';

import { useRouter } from 'next/navigation';

import { ClipboardList } from 'lucide-react';

import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Trans } from '@kit/ui/trans';

import { useBookingPresentation } from './booking-presentation';

interface ProfileCompletionDialogProps {
  /** Controlled open state — owned by the booking form so the popup surfaces
   *  only after the first-time-offer step and never stacks with it. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Labels of the required fields the customer still needs to fill. */
  missingFieldLabels: string[];
}

/**
 * Booking-page popup requiring a signed-in customer to complete required
 * profile fields before booking. Not dismissible — "Go to profile" (the Details
 * tab) is the only way forward, which keeps the rest of the app reachable.
 * Controlled by the parent so it can be sequenced after the first-time-offer
 * popup.
 */
export function ProfileCompletionDialog({
  open,
  onOpenChange,
  missingFieldLabels,
}: ProfileCompletionDialogProps) {
  const presentation = useBookingPresentation();
  const router = useRouter();

  const goToProfile = () => {
    onOpenChange(false);
    // Land directly on the "Profile Details" tab where the custom fields live.
    router.push('/profile?tab=details');
  };

  return (
    <Dialog open={open}>
      <DialogContent
        {...presentation}
        hideCloseButton
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        data-test="profile-completion-dialog"
      >
        <DialogHeader>
          <div className="from-primary/80 to-primary text-primary-foreground mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br shadow-sm">
            <ClipboardList className="h-5 w-5" />
          </div>
          <DialogTitle>
            <Trans i18nKey="common:profileCompletion.title" />
          </DialogTitle>
          <DialogDescription>
            <Trans i18nKey="common:profileCompletion.description" />
          </DialogDescription>
        </DialogHeader>

        {missingFieldLabels.length > 0 && (
          <ul className="text-muted-foreground list-disc space-y-1 ps-5 text-sm">
            {missingFieldLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button
            onClick={goToProfile}
            data-test="profile-completion-go-to-profile-button"
          >
            <Trans i18nKey="common:profileCompletion.goToProfile" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
